export type CookieConsent = {
  version: number;
  updatedAt: string;
  analytics: boolean;
};

const CONSENT_VERSION = 1;
const CONSENT_KEY = 'sor_cookie_consent_v1';

const normalizeMeasurementId = (value?: string) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const getMeasurementId = () => {
  const viteMeasurementId = normalizeMeasurementId(
    (import.meta as any).env?.VITE_GA_MEASUREMENT_ID as string | undefined,
  );
  if (viteMeasurementId) return viteMeasurementId;
  if (typeof window === 'undefined') return undefined;

  return normalizeMeasurementId(
    window.__RUNTIME_CONFIG__?.VITE_GA_MEASUREMENT_ID,
  );
};

export const readCookieConsent = (): CookieConsent | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsent;
    if (!parsed || parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const writeCookieConsent = (consent: CookieConsent) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
};

export const hasAnalyticsConsent = () => {
  const consent = readCookieConsent();
  return Boolean(consent?.analytics);
};

const injectGtagScript = (measurementId: string) => {
  if (document.getElementById('ga-gtag')) return;
  const script = document.createElement('script');
  script.id = 'ga-gtag';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);
};

export const initAnalytics = () => {
  if (typeof window === 'undefined') return;
  const measurementId = getMeasurementId();
  if (!measurementId) return;
  if (window.__gaInitialized) return;

  injectGtagScript(measurementId);

  window.dataLayer = window.dataLayer || [];
  const gtag = function gtag(...args: any[]) {
    window.dataLayer?.push(args);
  };
  window.gtag = window.gtag || gtag;
  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    anonymize_ip: true,
    send_page_view: false,
  });

  window.__gaInitialized = true;
};

export const disableAnalytics = () => {
  const measurementId = getMeasurementId();
  if (!measurementId || typeof window === 'undefined') return;
  (window as any)[`ga-disable-${measurementId}`] = true;
};

export const enableAnalytics = () => {
  const measurementId = getMeasurementId();
  if (!measurementId || typeof window === 'undefined') return;
  (window as any)[`ga-disable-${measurementId}`] = false;
  initAnalytics();
};

export const trackPageView = (path: string) => {
  const measurementId = getMeasurementId();
  if (!measurementId || typeof window === 'undefined') return;
  if (!window.gtag) return;

  window.gtag('event', 'page_view', {
    page_title: document.title,
    page_location: window.location.href,
    page_path: path,
  });
};
